import type { CellRange } from 'ag-grid-community';

import type {
  MergeDecisionDto,
  MergeRowDto,
  MergeSection,
} from '../api/mergeResolution.types';

import { periodKey } from './cellKey';
import { isLegal } from './legality';
import type { CellKey } from './types';

/**
 * Cells that a row-level rule targets in a given section.
 * - `needs_decision`: CONFLICT cells only
 * - `auto_resolved`: every non-UNCHANGED cell (an override)
 */
export function rowCellKeys(
  row: MergeRowDto,
  section: MergeSection
): CellKey[] {
  if (section === 'needs_decision') {
    // Only CONFLICT cells
    return row.Periods.filter((period) => period.Class === 'CONFLICT').map(
      (period) => periodKey(row, period)
    );
  }

  // auto_resolved: every non-UNCHANGED cell
  return row.Periods.filter((period) => period.Class !== 'UNCHANGED').map(
    (period) => periodKey(row, period)
  );
}

/**
 * Expand a row-level selection into individual cell decisions.
 * For each selected row, take the cells from the section rule, skip any where
 * the choice is illegal, and emit one decision per remaining cell.
 */
export function expandSelection(
  rows: MergeRowDto[],
  selectedRowIds: string[],
  section: MergeSection,
  choice: 'MINE' | 'THEIRS'
): MergeDecisionDto[] {
  const decisions: MergeDecisionDto[] = [];
  const selectedSet = new Set(selectedRowIds);

  for (const row of rows) {
    if (!selectedSet.has(row.Input_RowID)) {
      continue;
    }

    const targetKeys = rowCellKeys(row, section);

    for (const period of row.Periods) {
      const key = periodKey(row, period);
      if (!targetKeys.includes(key)) {
        continue;
      }

      if (!isLegal(period, choice)) {
        continue;
      }

      decisions.push({
        input_row_id: row.Input_RowID,
        date_id: period.DateID,
        choice,
      });
    }
  }

  return decisions;
}

/**
 * Extract dateId from a period column's colId.
 * Period columns use the pattern `period-{dateId ?? 'value'}`.
 */
function extractDateIdFromPeriodColId(colId: string): string | null {
  const prefix = 'period-';
  if (!colId.startsWith(prefix)) {
    return null;
  }
  const dateIdPart = colId.slice(prefix.length);
  return dateIdPart === 'value' ? null : dateIdPart;
}

/**
 * Map ag-grid cell ranges to a flat array of cells with rowId and dateId.
 * - Only period columns (colId pattern `period-*`) are included.
 * - Expand, Decisions, Status, and dimension columns are excluded.
 * - Overlapping or disjoint ranges are unioned without duplicates.
 * - Out-of-bounds row indexes are guarded.
 * - A range's `rowIndex` is a position in the grid's *displayed* model, which
 *   a filter, a sort or an expanded detail row makes different from a position
 *   in `rows`. Pass `rowAt` (the grid's own displayed-index lookup) whenever
 *   the ranges came from a live grid; without it the index is read against
 *   `rows`, which is only correct for an unfiltered, unsorted grid.
 */
export function cellsInRanges(
  rows: MergeRowDto[],
  ranges: CellRange[],
  periodKeys: (string | null)[],
  rowAt: (displayedIndex: number) => MergeRowDto | undefined = (index) =>
    rows[index]
): { rowId: string; dateId: string | null }[] {
  const seenCells = new Set<string>();
  const cells: { rowId: string; dateId: string | null }[] = [];

  for (const range of ranges) {
    // `startRow`/`endRow` are `RowPosition` objects, not numbers, and either
    // end may be the larger when the user drags upward. A range with no row
    // position contributes nothing rather than producing NaN bounds.
    const startIdx = range.startRow?.rowIndex;
    const endIdx = range.endRow?.rowIndex;
    if (startIdx === undefined || endIdx === undefined) {
      continue;
    }
    const minRowIdx = Math.min(startIdx, endIdx);
    const maxRowIdx = Math.max(startIdx, endIdx);

    // Filter to period columns only. `colId` is private on `Column`, so the
    // id has to come from `getColId()`.
    const periodColIds = (range.columns || [])
      .map((col) => col.getColId())
      .filter((colId) => colId && colId.startsWith('period-'));

    // Map each period column to its dateId
    const periodDateIds = periodColIds.map(extractDateIdFromPeriodColId);

    // For each row index and each period column
    for (let rowIdx = minRowIdx; rowIdx <= maxRowIdx; rowIdx++) {
      // Guard out-of-bounds, and positions with no row (a detail row, or a
      // grid that is not ready): fail closed rather than guess.
      if (rowIdx < 0) {
        continue;
      }

      const row = rowAt(rowIdx);
      if (!row) {
        continue;
      }

      for (const dateId of periodDateIds) {
        // Create a unique key for this cell
        const cellKey = `${row.Input_RowID}\x1f${dateId ?? ''}`;

        // Skip duplicates
        if (seenCells.has(cellKey)) {
          continue;
        }

        seenCells.add(cellKey);
        cells.push({
          rowId: row.Input_RowID,
          dateId,
        });
      }
    }
  }

  return cells;
}

/**
 * Expand ag-grid cell ranges into individual cell decisions.
 * - Uses cellsInRanges to extract the cells.
 * - Applies the section rule: CONFLICT for needs_decision, non-UNCHANGED for auto_resolved.
 * - Skips any cell where the choice is illegal.
 */
export function expandCellRange(
  rows: MergeRowDto[],
  ranges: CellRange[],
  periodKeys: (string | null)[],
  section: MergeSection,
  choice: 'MINE' | 'THEIRS',
  rowAt?: (displayedIndex: number) => MergeRowDto | undefined
): MergeDecisionDto[] {
  const cells = cellsInRanges(rows, ranges, periodKeys, rowAt);
  const decisions: MergeDecisionDto[] = [];

  // Map rowIds to rows for quick lookup
  const rowMap = new Map<string, MergeRowDto>();
  for (const row of rows) {
    rowMap.set(row.Input_RowID, row);
  }

  for (const cell of cells) {
    const row = rowMap.get(cell.rowId);
    if (!row) {
      continue;
    }

    // Find the period for this dateId
    const period = row.Periods.find((p) => p.DateID === cell.dateId);
    if (!period) {
      continue;
    }

    // Apply section rule
    const targetKeys = rowCellKeys(row, section);
    const key = periodKey(row, period);
    if (!targetKeys.includes(key)) {
      continue;
    }

    // Check if the choice is legal
    if (!isLegal(period, choice)) {
      continue;
    }

    decisions.push({
      input_row_id: row.Input_RowID,
      date_id: cell.dateId,
      choice,
    });
  }

  return decisions;
}
